import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import { Pie, Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import SubPageLayout from "../../components/shared/SubPageLayout";

import logger from '../../utils/logger';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
);

const RetirementJourney = () => {
  const navigate = useNavigate();

  // State for statistics
  const [statistics, setStatistics] = useState({
    platformStats: {
      averageClientAge: 62,
      totalClients: 1250,
      averageRetirementSavings: 850000,
      clientSatisfaction: 92,
      totalManagedAssets: 1250000000,
      averageReturnRate: 7.5,
    },
    retirementStages: {
      preRetirement: 45,
      transitionPhase: 25,
      inRetirement: 30,
    },
    financialHealth: {
      excellent: 35,
      good: 45,
      needsAttention: 20,
    },
    trends: {
      monthlyGrowth: 2.3,
      clientAcquisition: 15,
      retentionRate: 98,
    },
  });

  // State for AI chatbot
  const [chatInput, setChatInput] = useState("");
  const [chatHeight, setChatHeight] = useState(384); // Default height (96 * 4)

  // State for second chatbot
  const [chatInput2, setChatInput2] = useState("");
  const [chatHeight2, setChatHeight2] = useState(384); // Default height (96 * 4)

  // Add state for file attachment
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedFile2, setAttachedFile2] = useState(null);

  // State for AI chatbot
  const [chatHistory, setChatHistory] = useState([
    {
      role: "assistant",
      content:
        "Hello! How can I assist you with retirement planning today? For example, you can ask me to:",
      suggestions: [
        "Search for a client",
        "View retirement statistics",
        "Generate portfolio recommendations",
        "Schedule client reviews",
      ],
    },
    {
      role: "user",
      content: "I need to process a $10,000 withdrawal for John Smith",
    },
    {
      role: "assistant",
      content:
        "I'd be happy to assist you with this withdrawal. Please select the account:",
      accountOptions: [
        {
          id: "7890",
          name: "Charles Schwab Contributory IRA ending in 7890",
          isSelected: true,
          hasCheckmark: true,
        },
        { id: "7981", name: "Charles Schwab Roth IRA ending in 7981" },
      ],
    },
    {
      role: "assistant",
      content:
        "Okay, and please select the destination account for direct deposit:",
      accountOptions: [
        {
          id: "5578",
          name: "Huntington Bank checking account ending in 5578",
          isSelected: true,
          hasCheckmark: true,
        },
        { id: "new", name: "Add a new bank account", hasClickHere: true },
      ],
    },
    {
      role: "assistant",
      content: "Is this withdrawal gross or net?",
      accountOptions: [
        {
          id: "gross",
          name: "Gross ($11,363.63 withdrawn, $10,000 after taxes)",
          isSelected: true,
          hasCheckmark: true,
        },
        { id: "net", name: "Net ($10,000 withdrawn plus $1,363.63 for taxes)" },
      ],
      additionalInfo:
        "Gross means taxes are taken from the withdrawal amount. Net means taxes are in addition to the withdrawal amount.",
    },
    {
      role: "assistant",
      content:
        "The current tax withholdings on file are 12% Federal, 0% Illinois State for this gross withdrawal of $11,363.63. Would you like to proceed with these withholdings?",
      accountOptions: [
        {
          id: "proceed",
          name: "Yes, proceed with current withholdings",
          isSelected: true,
          hasCheckmark: true,
        },
        {
          id: "adjust",
          name: "No, I need to adjust the withholdings",
          hasClickHere: true,
        },
      ],
    },
    {
      role: "assistant",
      content:
        "Okay, this is a multi-step task because we will first need to raise cash. Here are the recommended trades to maintain risk tolerance and raise the necessary capital for distribution. Shares sold will take a full trading day to settle into cash, would you like to proceed with the trades or create a task reminder for later?",
      actionButton: {
        text: "Execute Trades",
        action: "executeTrades",
        completed: true,
        isSelected: true,
        hasCheckmark: true,
      },
      accountOptions: [
        { id: "reminder", name: "Create a task reminder for later" },
      ],
    },
    {
      role: "assistant",
      content:
        "I see those trades have been successfully executed. Now we can set up the distribution. Would you like to proceed with the distribution now or create a task reminder for later?",
      actionButton: {
        text: "Set Up Distribution",
        action: "setupDistribution",
        completed: true,
        isSelected: true,
        hasCheckmark: true,
      },
      accountOptions: [
        { id: "reminder", name: "Create a task reminder for later" },
      ],
    },
    {
      role: "assistant",
      content:
        "Great! We have successfully processed the following transaction:\n\n• Client: John Smith\n• Transaction Type: Gross withdrawal ($11,363.63 total)\n• Net Amount: $10,000.00\n• Tax Withholding: 12% Federal ($1,363.63), 0% Illinois State ($0.00)\n• Source Account: Charles Schwab Contributory IRA ending in 7890\n• Destination: Huntington Bank checking account ending in 5578\n• Settlement: 2-3 business days (standard ACH processing)\n\nThe necessary trades were executed to maintain the portfolio risk tolerance. Anything else I can help you with today?",
    },
    {
      role: "user",
      content: "No that will be all thank you.",
    },
  ]);

  // Second chat bot history - separate from the first one
  const [chatHistory2, setChatHistory2] = useState([
    {
      role: "assistant",
      content:
        "Hello! How can I assist you with retirement planning today? For example, you can ask me to:",
      suggestions: [
        "Search for a client",
        "View retirement statistics",
        "Generate portfolio recommendations",
        "Schedule client reviews",
      ],
    },
    {
      role: "user",
      content:
        "I need to process an immediate $2,000 withdrawal for Michael Smith's Charles Schwab Non-Qualified account ending in 7890 to the bank account on file.",
    },
    {
      role: "assistant",
      content:
        "I have reviewed the account and see that Michael already has the necessary cash available for this distribution and no taxes are required for this non-qualified account withdrawal. I also see the Huntington Bank account on file ending in 5578. Would you like to proceed with the distribution now or create a task reminder for later?",
      actionButton: {
        text: "Proceed with Distribution",
        action: "setupDistribution",
        completed: true,
        isSelected: true,
        hasCheckmark: true,
      },
      accountOptions: [
        { id: "reminder", name: "Create a task reminder for later" },
      ],
    },
    {
      role: "assistant",
      content:
        "Great! We have successfully processed the following transaction:\n\n• Client: Michael Smith\n• Transaction Type: Withdrawal\n• Amount: $2,000.00\n• Tax Withholding: None (not required for non-qualified account)\n• Source Account: Charles Schwab Non-Qualified account ending in 7890\n• Destination: Huntington Bank checking account ending in 5578\n• Settlement: 1-2 business days (standard ACH processing)\n\nIs there anything else I can help you with today?",
    },
    {
      role: "user",
      content: "No that will be all thank you.",
    },
  ]);

  // State for client search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    stage: "all",
    ageRange: "all",
    financialHealth: "all",
  });

  // Add state for section visibility
  const [showClientSearch, setShowClientSearch] = useState(false);

  // Mock client data
  const mockClients = [
    {
      id: 1,
      name: "John Smith",
      age: 65,
      stage: "transitionPhase",
      financialHealth: "excellent",
      retirementProgress: 85,
      nextReviewDate: "2025-04-15",
    },
    {
      id: 2,
      name: "Sarah Johnson",
      age: 58,
      stage: "preRetirement",
      financialHealth: "good",
      retirementProgress: 72,
      nextReviewDate: "2025-04-20",
    },
    {
      id: 3,
      name: "Michael Brown",
      age: 71,
      stage: "inRetirement",
      financialHealth: "excellent",
      retirementProgress: 95,
      nextReviewDate: "2025-04-10",
    },
  ];

  // Chart data
  const retirementStagesData = {
    labels: ["Pre-Retirement", "Transition Phase", "In Retirement"],
    datasets: [
      {
        data: [
          statistics.retirementStages.preRetirement,
          statistics.retirementStages.transitionPhase,
          statistics.retirementStages.inRetirement,
        ],
        backgroundColor: ["#4CAF50", "#FFA726", "#2196F3"],
        borderWidth: 1,
      },
    ],
  };

  const financialHealthData = {
    labels: ["Excellent", "Good", "Needs Attention"],
    datasets: [
      {
        data: [
          statistics.financialHealth.excellent,
          statistics.financialHealth.good,
          statistics.financialHealth.needsAttention,
        ],
        backgroundColor: ["#4CAF50", "#FFA726", "#f44336"],
        borderWidth: 1,
      },
    ],
  };

  // Effect to filter clients based on search and filters
  useEffect(() => {
    const filteredClients = mockClients.filter((client) => {
      const matchesSearch = client.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesStage =
        selectedFilters.stage === "all" ||
        client.stage === selectedFilters.stage;
      const matchesHealth =
        selectedFilters.financialHealth === "all" ||
        client.financialHealth === selectedFilters.financialHealth;

      return matchesSearch && matchesStage && matchesHealth;
    });
    setSearchResults(filteredClients);
  }, [searchQuery, selectedFilters]);

  // Handle chat submission
  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    // Add user message
    setChatHistory((prev) => [...prev, { role: "user", content: chatInput }]);

    // Simulate AI response
    setTimeout(() => {
      setChatHistory((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I understand your request. Is there anything specific you need help with regarding administrative tasks?",
        },
      ]);
    }, 1000);

    setChatInput("");
  };

  // Handle chat suggestion click
  const handleSuggestionClick = (suggestion) => {
    setChatHistory((prev) => [
      ...prev,
      { role: "user", content: suggestion },
      {
        role: "assistant",
        content: `I'll help you ${suggestion.toLowerCase()}. What specific information would you like to see?`,
        suggestions: getSuggestionsByType(suggestion),
      },
    ]);
  };

  // Get contextual suggestions based on chat type
  const getSuggestionsByType = (type) => {
    switch (type.toLowerCase()) {
      case "search for a client":
        return [
          "View all clients",
          "Search by name",
          "Filter by retirement stage",
          "Show recent clients",
        ];
      case "view retirement statistics":
        return [
          "Platform overview",
          "Client demographics",
          "Performance metrics",
          "Trend analysis",
        ];
      case "generate portfolio recommendations":
        return [
          "Risk analysis",
          "Asset allocation",
          "Rebalancing suggestions",
          "Tax optimization",
        ];
      case "schedule client reviews":
        return [
          "View calendar",
          "Next available slots",
          "Upcoming reviews",
          "Past due reviews",
        ];
      default:
        return [];
    }
  };

  // Handle action button click
  const handleActionButtonClick = (action) => {
    // In a real application, this would trigger the appropriate modal or action
    logger.info(`Action triggered: ${action}`);
    // For now, we'll just acknowledge the action was taken
    if (action === "executeTrades") {
      // Actions would be implemented here
    } else if (action === "setupDistribution") {
      // Actions would be implemented here
    }
  };

  // Handle account option selection
  const handleAccountSelection = (option) => {
    // Display just the ID for account selections or the full name for other selections
    const displayContent = option.id.match(/^\d+$/) ? option.id : option.name;

    setChatHistory((prev) => [
      ...prev,
      {
        role: "user",
        content: displayContent,
        isSelection: true,
        fullSelection: option.name,
      },
    ]);
  };

  // Handle chat resize
  const handleChatResize = (newSize) => {
    setChatHeight(newSize);
  };

  // Function to scroll to client search
  const scrollToClientSearch = () => {
    setShowClientSearch(true);
    setTimeout(() => {
      document
        .getElementById("clientSearchSection")
        ?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Handle file attachment
  const handleFileAttachment = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachedFile(file);
      // In a real application, you would handle the file upload here
      logger.info(`File attached: ${file.name}`);
    }
  };

  // Handle chat submission for second chatbot
  const handleChatSubmit2 = (e) => {
    e.preventDefault();
    if (!chatInput2.trim()) return;

    // Add user message
    setChatHistory2((prev) => [...prev, { role: "user", content: chatInput2 }]);

    // Simulate AI response
    setTimeout(() => {
      setChatHistory2((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I understand your request. Is there anything specific you need help with regarding administrative tasks?",
        },
      ]);
    }, 1000);

    setChatInput2("");
  };

  // Handle chat resize for second chatbot
  const handleChatResize2 = (newSize) => {
    setChatHeight2(newSize);
  };

  // Handle file attachment for second chatbot
  const handleFileAttachment2 = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachedFile2(file);
      // In a real application, you would handle the file upload here
      logger.info(`File attached to second chatbot: ${file.name}`);
    }
  };

  // Handle account option selection for second chat
  const handleAccountSelection2 = (option) => {
    // Display just the ID for account selections or the full name for other selections
    const displayContent = option.id.match(/^\d+$/) ? option.id : option.name;

    setChatHistory2((prev) => [
      ...prev,
      {
        role: "user",
        content: displayContent,
        isSelection: true,
        fullSelection: option.name,
      },
    ]);
  };

  // Handle action button click for second chat
  const handleActionButtonClick2 = (action) => {
    logger.info(`Action triggered in second chat: ${action}`);
    // Implementation would go here
  };

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Financial Planning", path: "/financial-planning" },
        {
          label: "Retirement Journey",
          path: "/financial-planning/retirement-journey",
        },
      ]}
      title="Retirement Journey"
      subtitle="Comprehensive retirement planning and analysis tools"
    >
      <div className="max-w-7xl mx-auto">
        {/* Top Row - Statistics and Charts */}
        <div className="grid grid-cols-3 gap-8 mb-8">
          {/* Platform Statistics */}
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Platform Statistics
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Average Client Age</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {statistics.platformStats.averageClientAge}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Total Clients</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {statistics.platformStats.totalClients}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Average Retirement Savings
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  $
                  {statistics.platformStats.averageRetirementSavings.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Client Satisfaction</p>
                <p className="text-2xl font-bold text-green-600">
                  {statistics.platformStats.clientSatisfaction}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Total Managed Assets</p>
                <p className="text-2xl font-bold text-[#202EC6]">
                  $
                  {(
                    statistics.platformStats.totalManagedAssets / 1000000000
                  ).toFixed(2)}
                  B
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-500">Average Return Rate</p>
                <p className="text-2xl font-bold text-green-600">
                  {statistics.platformStats.averageReturnRate}%
                </p>
              </div>
            </div>
          </div>

          {/* Retirement Stages */}
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Retirement Stages
            </h2>
            <div className="h-64">
              <Pie
                data={retirementStagesData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Financial Health */}
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Financial Health
            </h2>
            <div className="h-64">
              <Pie
                data={financialHealthData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>

        {/* Middle Row - AI Chat Bot */}
        <div className="mb-8">
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-white to-gray-50 flex items-center">
              <div className="w-10 h-10 rounded-full bg-[#202EC6] text-white flex items-center justify-center mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-light text-gray-900 dark:text-gray-100 tracking-wide">
                AI Administrative Assistant
              </h2>
            </div>
            <div
              className="p-6 flex flex-col relative"
              style={{
                height: `${chatHeight}px`,
                resize: "vertical",
                overflowY: "auto",
                overflowX: "hidden",
                minHeight: "250px",
                width: "100%",
              }}
            >
              <div className="flex-1 overflow-y-auto overflow-x-hidden mb-4 space-y-4 w-full">
                {chatHistory.map((message, index) => (
                  <div key={index} className="w-full">
                    <div
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} w-full`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 relative ${
                          message.role === "user"
                            ? message.isSelection
                              ? message.isHighlighted
                                ? "bg-blue-200 text-gray-900 dark:text-gray-100 border border-blue-400 font-medium"
                                : "bg-blue-100 text-gray-900 dark:text-gray-100 border border-blue-300 font-medium"
                              : "bg-[#202EC6] text-white"
                            : "bg-gray-100 text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {message.content}
                        {message.fullSelection && (
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-500 italic">
                            {message.fullSelection}
                          </div>
                        )}
                        {message.isHighlighted && message.isSelection && (
                          <div className="absolute -right-1 -top-1 bg-green-500 rounded-full p-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}
                        {message.additionalInfo && (
                          <div className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 dark:text-gray-300 rounded">
                            {message.additionalInfo}
                          </div>
                        )}
                      </div>
                    </div>
                    {message.suggestions && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message.suggestions.map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="px-3 py-1 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-full text-sm hover:bg-gray-200"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                    {message.accountOptions && (
                      <div className="mt-2 flex flex-col gap-2 pl-4 w-full">
                        {message.accountOptions.map((option, i) => (
                          <button
                            key={i}
                            onClick={() => handleAccountSelection(option)}
                            className={`px-4 py-2 text-left border text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-[#202EC6] transition-colors flex justify-between items-center relative w-1/5 ml-auto
                              ${option.isSelected ? "bg-blue-100 border-blue-300" : "bg-surface-default dark:bg-surface-default border-gray-300"}`}
                          >
                            <span>{option.name}</span>
                            {option.hasClickHere && (
                              <span className="ml-2 text-xs text-[#202EC6] underline whitespace-nowrap">
                                Click here
                              </span>
                            )}
                            {option.hasCheckmark && (
                              <div className="absolute -right-1 -top-1 bg-green-500 rounded-full p-0.5">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {message.actionButton && (
                      <div className="mt-2 flex justify-end items-center pl-4 w-full">
                        <button
                          onClick={() =>
                            handleActionButtonClick(message.actionButton.action)
                          }
                          className={`px-4 py-2 text-left border rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-[#202EC6] transition-colors flex justify-between items-center relative w-1/5
                            ${message.actionButton.isSelected ? "bg-blue-100 border-blue-300 text-gray-700 dark:text-gray-300" : "bg-surface-default dark:bg-surface-default border-[#202EC6] text-[#202EC6]"}`}
                        >
                          <span>{message.actionButton.text}</span>
                          <span className="ml-2 text-xs underline">
                            Click here
                          </span>
                          {message.actionButton.hasCheckmark && (
                            <div className="absolute -right-1 -top-1 bg-green-500 rounded-full p-0.5">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <form onSubmit={handleChatSubmit} className="flex gap-2 w-full">
                <div className="relative flex-1 flex items-center">
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer absolute left-3 text-gray-500 dark:text-gray-500 hover:text-[#202EC6]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="sr-only">Attach file</span>
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    onChange={handleFileAttachment}
                  />
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Ask me anything..."
                    className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#202EC6]"
                  />
                  {attachedFile && (
                    <div className="absolute right-3 flex items-center bg-blue-100 rounded-full px-2 py-0.5 text-xs text-blue-800">
                      <span className="truncate max-w-[100px]">
                        {attachedFile.name}
                      </span>
                      <button
                        type="button"
                        className="ml-1 text-blue-600 hover:text-blue-800"
                        onClick={() => setAttachedFile(null)}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90 whitespace-nowrap"
                >
                  Send
                </button>
              </form>
              <div className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize bg-gray-200 opacity-70 rounded-tl-sm"></div>
            </div>
          </div>
        </div>

        {/* Bottom Row - Action Cards */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <button className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow hover:shadow-md dark:shadow-none transition-all transform hover:-translate-y-1">
            <div className="text-4xl mb-2">📊</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              View Statistics
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Analyze retirement trends and metrics
            </p>
            <div className="mt-4 text-[#202EC6] text-sm">
              Click to explore →
            </div>
          </button>
          <button className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow hover:shadow-md dark:shadow-none transition-all transform hover:-translate-y-1">
            <div className="text-4xl mb-2">💡</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Insights
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">AI-driven recommendations</p>
            <div className="mt-4 text-[#202EC6] text-sm">View insights →</div>
          </button>
          <button
            onClick={scrollToClientSearch}
            className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow hover:shadow-md dark:shadow-none transition-all transform hover:-translate-y-1"
          >
            <div className="text-4xl mb-2">🔍</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Client Search
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Find and manage client journeys
            </p>
            <div className="mt-4 text-[#202EC6] text-sm">Click to search →</div>
          </button>
          <button className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow hover:shadow-md dark:shadow-none transition-all transform hover:-translate-y-1">
            <div className="text-4xl mb-2">📈</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Portfolio Suggestions
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Get tailored adjustments</p>
            <div className="mt-4 text-[#202EC6] text-sm">
              View suggestions →
            </div>
          </button>
        </div>

        {/* Duplicate Chat Bot */}
        <div className="mb-8">
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-white to-gray-50 flex items-center">
              <div className="w-10 h-10 rounded-full bg-[#202EC6] text-white flex items-center justify-center mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-light text-gray-900 dark:text-gray-100 tracking-wide">
                AI Administrative Assistant
              </h2>
            </div>
            <div
              className="p-6 flex flex-col relative"
              style={{
                height: `${chatHeight2}px`,
                resize: "vertical",
                overflowY: "auto",
                overflowX: "hidden",
                minHeight: "250px",
                width: "100%",
              }}
            >
              <div className="flex-1 overflow-y-auto overflow-x-hidden mb-4 space-y-4 w-full">
                {chatHistory2.map((message, index) => (
                  <div key={index} className="w-full">
                    <div
                      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} w-full`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 relative ${
                          message.role === "user"
                            ? message.isSelection
                              ? message.isHighlighted
                                ? "bg-blue-200 text-gray-900 dark:text-gray-100 border border-blue-400 font-medium"
                                : "bg-blue-100 text-gray-900 dark:text-gray-100 border border-blue-300 font-medium"
                              : "bg-[#202EC6] text-white"
                            : "bg-gray-100 text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {message.content}
                        {message.fullSelection && (
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-500 italic">
                            {message.fullSelection}
                          </div>
                        )}
                        {message.isHighlighted && message.isSelection && (
                          <div className="absolute -right-1 -top-1 bg-green-500 rounded-full p-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        )}
                        {message.additionalInfo && (
                          <div className="mt-2 p-2 bg-gray-50 text-sm text-gray-700 dark:text-gray-300 rounded">
                            {message.additionalInfo}
                          </div>
                        )}
                      </div>
                    </div>
                    {message.suggestions && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {message.suggestions.map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="px-3 py-1 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-full text-sm hover:bg-gray-200"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                    {message.accountOptions && (
                      <div className="mt-2 flex flex-col gap-2 pl-4 w-full">
                        {message.accountOptions.map((option, i) => (
                          <button
                            key={i}
                            onClick={() => handleAccountSelection2(option)}
                            className={`px-4 py-2 text-left border text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-[#202EC6] transition-colors flex justify-between items-center relative w-1/5 ml-auto
                              ${option.isSelected ? "bg-blue-100 border-blue-300" : "bg-surface-default dark:bg-surface-default border-gray-300"}`}
                          >
                            <span>{option.name}</span>
                            {option.hasClickHere && (
                              <span className="ml-2 text-xs text-[#202EC6] underline whitespace-nowrap">
                                Click here
                              </span>
                            )}
                            {option.hasCheckmark && (
                              <div className="absolute -right-1 -top-1 bg-green-500 rounded-full p-0.5">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {message.actionButton && (
                      <div className="mt-2 flex justify-end items-center pl-4 w-full">
                        <button
                          onClick={() =>
                            handleActionButtonClick2(
                              message.actionButton.action,
                            )
                          }
                          className={`px-4 py-2 text-left border rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-[#202EC6] transition-colors flex justify-between items-center relative w-1/5
                            ${message.actionButton.isSelected ? "bg-blue-100 border-blue-300 text-gray-700 dark:text-gray-300" : "bg-surface-default dark:bg-surface-default border-[#202EC6] text-[#202EC6]"}`}
                        >
                          <span>{message.actionButton.text}</span>
                          <span className="ml-2 text-xs underline">
                            Click here
                          </span>
                          {message.actionButton.hasCheckmark && (
                            <div className="absolute -right-1 -top-1 bg-green-500 rounded-full p-0.5">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <form onSubmit={handleChatSubmit2} className="flex gap-2 w-full">
                <div className="relative flex-1 flex items-center">
                  <label
                    htmlFor="file-upload-2"
                    className="cursor-pointer absolute left-3 text-gray-500 dark:text-gray-500 hover:text-[#202EC6]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="sr-only">Attach file</span>
                  </label>
                  <input
                    id="file-upload-2"
                    type="file"
                    className="hidden"
                    onChange={handleFileAttachment2}
                  />
                  <input
                    type="text"
                    value={chatInput2}
                    onChange={(e) => setChatInput2(e.target.value)}
                    placeholder="Ask me anything..."
                    className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#202EC6]"
                  />
                  {attachedFile2 && (
                    <div className="absolute right-3 flex items-center bg-blue-100 rounded-full px-2 py-0.5 text-xs text-blue-800">
                      <span className="truncate max-w-[100px]">
                        {attachedFile2.name}
                      </span>
                      <button
                        type="button"
                        className="ml-1 text-blue-600 hover:text-blue-800"
                        onClick={() => setAttachedFile2(null)}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90 whitespace-nowrap"
                >
                  Send
                </button>
              </form>
              <div className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize bg-gray-200 opacity-70 rounded-tl-sm"></div>
            </div>
          </div>
        </div>

        {/* Client Search Section */}
        <div
          id="clientSearchSection"
          className={`transition-opacity duration-500 ${showClientSearch ? "opacity-100" : "opacity-0"}`}
        >
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Client Search
              </h2>
            </div>
            <div className="p-6">
              <div className="flex gap-4 mb-6">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search clients..."
                    className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#202EC6] text-gray-900 dark:text-gray-100"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <select
                  value={selectedFilters.stage}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      stage: e.target.value,
                    }))
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#202EC6] text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Stages</option>
                  <option value="preRetirement">Pre-Retirement</option>
                  <option value="transitionPhase">Transition Phase</option>
                  <option value="inRetirement">In Retirement</option>
                </select>
                <select
                  value={selectedFilters.financialHealth}
                  onChange={(e) =>
                    setSelectedFilters((prev) => ({
                      ...prev,
                      financialHealth: e.target.value,
                    }))
                  }
                  className="rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#202EC6] text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Health Statuses</option>
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="needsAttention">Needs Attention</option>
                </select>
              </div>

              {/* Client Results */}
              <div className="space-y-4">
                {searchResults.map((client) => (
                  <div
                    key={client.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {client.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Age: {client.age}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            client.financialHealth === "excellent"
                              ? "bg-green-100 text-green-800"
                              : client.financialHealth === "good"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                          }`}
                        >
                          {client.financialHealth.charAt(0).toUpperCase() +
                            client.financialHealth.slice(1)}
                        </span>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-200">
                            Message
                          </button>
                          <button className="px-3 py-1 bg-[#202EC6] text-white rounded-lg text-sm hover:bg-opacity-90">
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Retirement Progress:
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {client.retirementProgress}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#202EC6] rounded-full transition-all duration-500"
                          style={{ width: `${client.retirementProgress}%` }}
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                      <p>
                        Next Review:{" "}
                        {new Date(client.nextReviewDate).toLocaleDateString()}
                      </p>
                      <div className="flex space-x-4">
                        <span>
                          Stage:{" "}
                          {client.stage.replace(/([A-Z])/g, " $1").trim()}
                        </span>
                        <span>|</span>
                        <button className="text-[#202EC6] hover:underline">
                          View Timeline
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default RetirementJourney;
